/**
 * Pure helpers for the sessions of a project (the workspace's Sessions tab) and the kanban's collaboration bits: which
 * chats belong to a project, the live cards of other people's AI agents, the "Everyone / AI only" filter.
 */
import { parseVfsSessionId } from '../services/AdkSessionsService';
import { agentName, agentStepLabel, firstName, modelLabel } from './presenceModel';

/** The project a chat session belongs to: its `vfs-<project>` id, else what the by-conversation lookup found. */
export function projectOfSession(session, lookedUp = {}) {
  return parseVfsSessionId(session?.id)?.projectId || lookedUp[session?.id] || null;
}

/** The chats of one project, newest first. `lookedUp`: `{ [sessionId]: projectId }`. */
export function sessionsOfProject(sessions, projectId, lookedUp = {}) {
  return (sessions || [])
    .filter((session) => projectOfSession(session, lookedUp) === projectId)
    .sort((a, b) => (b.lastUpdateTime || 0) - (a.lastUpdateTime || 0));
}

/** The chat sessions still worth a by-conversation lookup: not `vfs-` ids, not looked up yet, the `max` newest. */
export function sessionsToLookUp(sessions, lookedUp = {}, max = 25) {
  return [...(sessions || [])]
    .filter((session) => session?.id && !parseVfsSessionId(session.id) && !(session.id in lookedUp))
    .sort((a, b) => (b.lastUpdateTime || 0) - (a.lastUpdateTime || 0))
    .slice(0, max);
}

const base = (path) => String(path || '').split('/').pop();

/**
 * Other people's AI agents at work in a project, as kanban cards (you never see their prompts, only the title of what
 * runs and the files): `{ key, agent, title, detail, driverName }`. `projectName` labels the card.
 */
export function liveAgentCards(snapshot, { projectName = '' } = {}) {
  const you = snapshot?.you;
  return (snapshot?.agents || [])
    .filter((agent) => (agent.paths || []).length > 0 || agent.paused)
    .filter((agent) => !you || agent.driver?.id !== you.id)
    .map((agent) => {
      const extra = [agentStepLabel(agent), modelLabel(agent.model)].filter(Boolean).map((part) => ` · ${part}`).join('');
      return {
        key: `live-agent:${agent.id}`,
        agent,
        title: agent.driver ? `${firstName(agent.driver.name)}’s ${agent.label || 'AI agent'}` : agentName(agent, you),
        detail: agent.paused
          ? 'paused'
          : `writing ${(agent.paths || []).map(base).slice(0, 2).join(', ')}${extra}`,
        project: projectName,
        sessionId: agent.sessionId || null,
      };
    });
}

/**
 * The kanban's "Whose sessions" filter: 'mine' (your chats and AI tools), 'everyone' (also the live work of others in
 * your shared projects), 'ai' (only what an AI agent runs: running chats, AI tool sessions, others' agents).
 */
export const SESSION_FILTERS = [
  { value: 'mine', label: 'Mine' },
  { value: 'everyone', label: 'Everyone' },
  { value: 'ai', label: 'AI only' },
];

/** The chats a filter keeps: 'ai' only the running ones (an agent working), else all. */
export function chatsForFilter(sessions, activeIds, filter) {
  if (filter !== 'ai') { return sessions || []; }
  return (sessions || []).filter((session) => activeIds?.has?.(session.id));
}
